import { useEffect, useState } from "react";
import { BarChart, Bar, LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { MessageSquare, UserPlus, Terminal, Shield, Hash } from "lucide-react";
import api from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { StatCard, PageHead } from "@/components/kit";

export default function Analytics() {
  const { guildId, refreshKey } = useGuild();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/guilds/${guildId}/analytics`).then((r) => setData(r.data)); }, [guildId, refreshKey]);
  if (!data) return <p className="text-zinc-500">Loading…</p>;
  const t = data.totals;
  const isAdmin = data.scope === "admin";

  return (
    <div>
      <PageHead title="Analytics" desc={isAdmin ? "Community growth and engagement over the last 14 days." : "Your community's growth over the last 14 days."} />
      {!isAdmin && (
        <div className="bg-zinc-900 border border-zinc-800 text-zinc-400 text-xs rounded-lg px-4 py-2.5 mb-6">
          You're viewing the owner summary. Full analytics (command usage & moderation breakdowns) are available to platform admins.
        </div>
      )}
      <div className={`grid grid-cols-2 ${isAdmin ? "lg:grid-cols-4" : "lg:grid-cols-2"} gap-4 mb-6`}>
        <StatCard icon={MessageSquare} label="Messages" value={t.messages.toLocaleString()} />
        <StatCard icon={UserPlus} label="New Members" value={t.members} accent="#f59e0b" />
        {isAdmin && <StatCard icon={Terminal} label="Commands Run" value={t.commands} accent="#facc15" />}
        {isAdmin && <StatCard icon={Shield} label="Mod Actions" value={t.mod} accent="#ef4444" />}
      </div>
      <div className={`grid grid-cols-1 ${isAdmin ? "lg:grid-cols-2" : ""} gap-6`}>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-head font-semibold mb-4">Messages &amp; Members</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.timeseries}>
              <CartesianGrid stroke="#94a3b8" strokeOpacity={0.2} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#71717a", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "rgb(var(--z-900))", border: "1px solid rgb(var(--z-800))", borderRadius: 8 }} />
              <Line type="monotone" dataKey="messages" stroke="#0EA5E9" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="members" stroke="#f59e0b" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        {isAdmin && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h3 className="font-head font-semibold mb-4">Command Usage</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.command_usage}>
              <CartesianGrid stroke="#94a3b8" strokeOpacity={0.2} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#71717a", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#71717a", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "rgb(var(--z-900))", border: "1px solid rgb(var(--z-800))", borderRadius: 8 }} cursor={{ fill: "#27272a55" }} />
              <Bar dataKey="uses" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        )}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mt-6" data-testid="top-channels-widget">
        <h3 className="font-head font-semibold mb-4 flex items-center gap-2"><Hash className="w-4 h-4 text-volt" /> Most Active Channels</h3>
        {(!data.top_channels || data.top_channels.length === 0) ? (
          <p className="text-sm text-zinc-500">No channel activity recorded yet — this fills in as the bot tracks messages on your server.</p>
        ) : (
          <div className="space-y-2.5">
            {(() => { const max = Math.max(...data.top_channels.map((c) => c.count), 1);
              return data.top_channels.map((c, i) => (
                <div key={i} data-testid={`top-channel-${i}`} className="flex items-center gap-3">
                  <span className="text-sm text-zinc-300 w-40 truncate">#{c.name}</span>
                  <div className="flex-1 h-2.5 bg-black rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-volt to-[#f59e0b]" style={{ width: `${(c.count / max) * 100}%` }} />
                  </div>
                  <span className="text-xs text-zinc-400 w-16 text-right">{c.count.toLocaleString()}</span>
                </div>
              )); })()}
          </div>
        )}
      </div>
    </div>
  );
}
